// ======================================================================
// 📁 File: src/modules/ppe/pages/resources/skill/SkillDrawer.jsx
// 📝 Purpose:
//   Skill Master – Create / Edit Drawer
//   - SAME UX AS MachineGroupDrawer
//   - SAFE lifecycle handling (status change separated)
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";

export default function SkillDrawer({
  open,
  mode,
  record,
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    skill_code: "",
    skill_name: "",
    level_required: false,
    is_active: true,
  });

  const [saving, setSaving] = useState(false);

  // ------------------------------------------------------------
  // Init form
  // ------------------------------------------------------------
  useEffect(() => {
    if (isEdit && record) {
      setForm({
        skill_code: record.skill_code || "",
        skill_name: record.skill_name || "",
        level_required: !!record.level_required,
        is_active: !!record.is_active,
      });
    } else {
      setForm({
        skill_code: "",
        skill_name: "",
        level_required: false,
        is_active: true,
      });
    }
  }, [isEdit, record, open]);

  // ------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ------------------------------------------------------------
  // Save handler (SAFE lifecycle handling)
  // ------------------------------------------------------------
  const handleSubmit = async () => {
    if (!form.skill_code || !form.skill_name) {
      alert("Skill code and name are required");
      return;
    }

    setSaving(true);

    try {
      if (isEdit) {
        // ----------------------------------
        // 1️⃣ Normal update (NO is_active)
        // ----------------------------------
        await axiosClient.put(
          `/api/ppe/masters/skills/${record.id}`,
          {
            skill_name: form.skill_name,
            level_required: form.level_required,
          }
        );

        // ----------------------------------
        // 2️⃣ Status change (if toggled)
        // ----------------------------------
        if (form.is_active !== record.is_active) {
          await axiosClient.patch(
            `/api/ppe/masters/skills/${record.id}/status`,
            {
              is_active: form.is_active,
            }
          );
        }
      } else {
        // ----------------------------------
        // CREATE
        // ----------------------------------
        await axiosClient.post(
          "/api/ppe/masters/skills",
          {
            skill_code: form.skill_code,
            skill_name: form.skill_name,
            level_required: form.level_required,
          }
        );
      }

      onClose();
      onSaved();
    } catch (err) {
      console.error("Failed to save skill", err);
      alert("Failed to save skill");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="drawer-backdrop">
      <div className="drawer">
        {/* ================= Header ================= */}
        <div className="drawer-header">
          <h5>{isEdit ? "Edit Skill" : "Create Skill"}</h5>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* ================= Body ================= */}
        <div className="drawer-body">
          <div className="mb-3">
            <label className="form-label">Skill Code</label>
            <input
              name="skill_code"
              className="form-control"
              value={form.skill_code}
              onChange={handleChange}
              disabled={isEdit}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Skill Name</label>
            <input
              name="skill_name"
              className="form-control"
              value={form.skill_name}
              onChange={handleChange}
            />
          </div>

          <div className="form-check mb-3">
            <input
              type="checkbox"
              className="form-check-input"
              name="level_required"
              checked={form.level_required}
              onChange={handleChange}
            />
            <label className="form-check-label">
              Level Required
            </label>
          </div>

          {isEdit && (
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
              />
              <label className="form-check-label">
                Active
              </label>
            </div>
          )}
        </div>

        {/* ================= Footer ================= */}
        <div className="drawer-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
